import { useState, useMemo, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Download, ChevronUp, ChevronDown, Search, FileDown, FileText, Columns, Check, AlertTriangle } from "lucide-react";
import { useComponents, useScans } from "@/hooks";
import { FindComponentsOptions, Scan, ScanComponent, Component } from "@/api";
import { Paginator } from "@/components/Paginator";
import { MultiSelect } from "@/components/ui/multi-select";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useApplicationList } from "@/hooks/useApplications";
import { useProjectList } from "@/hooks/useProjects";
import { ComponentDetailsModal } from "@/components/ComponentDetailsModal";
import { LicenseObligationsModal } from "@/components/LicenseObligationsModal";
import { scoreToCategory, getScoreCategoryOptions } from "@/components/charts/distributionUtils";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuCheckboxItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { EntitlementGate } from "@/components/EntitlementGate";


interface ColumnConfig {
  key: keyof Component;
  label: string;
  sortable?: boolean;
  filterable?: boolean;
  render?: (value: any, row: Component) => React.ReactNode;
}

const columns: ColumnConfig[] = [
  { key: "applicationName", label: "Application Name", sortable: true, filterable: true },
  { key: "projectName", label: "Project Name", sortable: true, filterable: true },
  { key: "scanVersion", label: "Scan Version", sortable: true, filterable: true },
  { key: "name", label: "Component Name", sortable: true, filterable: true },
  { key: "version", label: "Component Version", sortable: true, filterable: true },
  { key: "license_spdx_identifier", label: "License", sortable: true, filterable: true },
  { key: "isdeprecated", label: "Deprecated?", sortable: true, filterable: true },
  { key: "total_vulnerabilities", label: "Vulnerabilities", sortable: true, filterable: true },
  { key: "risk_level", label: "Risk Level", sortable: true, filterable: true },
  { key: "homepageurl", label: "Homepage URL", sortable: true, filterable: true },
  { key: "avg_cvss_score", label: "Avg CVSS", sortable: true, filterable: true },
  { key: "maintenance_status", label: "Maintenance", sortable: true, filterable: true },
  { key: "overallscore", label: "Overall Score", sortable: true, filterable: true },
  { key: "vulnerabilityscore", label: "Vuln Score", sortable: true, filterable: true },
  { key: "description", label: "Description", sortable: true, filterable: true },
  { key: "isexploitavailable", label: "Exploit Available?", sortable: true, filterable: true },
  { key: "isfixavailable", label: "Fix Available?", sortable: true, filterable: true },
  { key: "isoutdated", label: "Outdated?", sortable: true, filterable: true },
  { key: "repourl", label: "Repository URL", sortable: true, filterable: true },
  { key: "licensescore", label: "License Score", sortable: true, filterable: true },
  { key: "operationalriskscore", label: "Operational Risk", sortable: true, filterable: true },
  { key: "technicaldebtscore", label: "Technical Debt", sortable: true, filterable: true },
  { key: "license_risk_level", label: "License Risk", sortable: true, filterable: true },
  { key: "max_cvss_score", label: "Max CVSS", sortable: true, filterable: true },
  { key: "critical_vulnerabilities", label: "Critical Vulns", sortable: true, filterable: true },
  { key: "high_vulnerabilities", label: "High Vulns", sortable: true, filterable: true },
  { key: "medium_vulnerabilities", label: "Medium Vulns", sortable: true, filterable: true },
  { key: "low_vulnerabilities", label: "Low Vulns", sortable: true, filterable: true },
  { key: "total_license_issues", label: "License Issues", sortable: true, filterable: true },
  { key: "avg_epss_score", label: "Avg EPSS", sortable: true, filterable: true },
  { key: "max_epss_score", label: "Max EPSS", sortable: true, filterable: true },
  { key: "unique_license_types", label: "License Types", sortable: true, filterable: true },
  { key: "risk_score", label: "Risk Score", sortable: true, filterable: true },
  { key: "compliance_score", label: "Compliance Score", sortable: true, filterable: true },
  { key: "security_maturity_score", label: "Security Maturity", sortable: true, filterable: true },
  { key: "githubstars", label: "Stars", sortable: true, filterable: true },
  { key: "githubcontributors", label: "Contributors", sortable: true, filterable: true },
  { key: "githubforks", label: "Forks", sortable: true, filterable: true }
];

const numericColumns = new Set([
  'total_vulnerabilities', 'avg_cvss_score', 'overallscore', 'vulnerabilityscore',
  'licensescore', 'operationalriskscore', 'technicaldebtscore', 'cvssscore',
  'max_cvss_score', 'critical_vulnerabilities', 'high_vulnerabilities', 'medium_vulnerabilities',
  'low_vulnerabilities', 'total_license_issues', 'avg_epss_score', 'max_epss_score',
  'unique_license_types', 'risk_score', 'compliance_score', 'security_maturity_score'
]);

const scoreBasedColumns = new Set([
  'overallscore', 'vulnerabilityscore', 'operationalriskscore', 'licensescore', 'technicaldebtscore'
]);

export default function ComponentsHub({ workgroupId, applicationId: propApplicationId, projectId: propProjectId }: { workgroupId: number, applicationId?: number; projectId?: number }) {
  const [searchParams] = useSearchParams();
  const queryApplicationId = searchParams.get('applicationId');
  const queryProjectId = searchParams.get('projectId');
  const applicationId = propApplicationId || (queryApplicationId ? parseInt(queryApplicationId) : undefined);
  const projectId = propProjectId || (queryProjectId ? parseInt(queryProjectId) : undefined);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortConfig, setSortConfig] = useState<{ key: keyof Component; direction: 'asc' | 'desc' } | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedComponent, setSelectedComponent] = useState<any | null>(null);
  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);
  const [selectedLicenseId, setSelectedLicenseId] = useState<number | null>(null);
  const [isObligationsModalOpen, setIsObligationsModalOpen] = useState(false);
  const [columnFilters, setColumnFilters] = useState<Partial<Record<keyof Component, string>>>({
    applicationName: '',
    projectName: '',
    scanVersion: '',
    name: '',
    version: '',
    license_spdx_identifier: '',
    isdeprecated: '',
    total_vulnerabilities: '',
    risk_level: '',
    homepageurl: '',
    maintenance_status: '',
    overallscore: '',
    vulnerabilityscore: '',
    avg_cvss_score: '',
    description: '',
    isexploitavailable: '',
    isfixavailable: '',
    isoutdated: '',
    repourl: '',
    licensescore: '',
    operationalriskscore: '',
    technicaldebtscore: '',
    license_risk_level: '',
    cvssscore: '',
    max_cvss_score: '',
    critical_vulnerabilities: '',
    high_vulnerabilities: '',
    medium_vulnerabilities: '',
    low_vulnerabilities: '',
    total_license_issues: '',
    avg_epss_score: '',
    max_epss_score: '',
    unique_license_types: '',
    risk_score: '',
    compliance_score: '',
    security_maturity_score: '',
    githubstars: '',
    githubcontributors: '',
    githubforks: ''
  });
  const [tableFilters, setTableFilters] = useState<{
    risk_level: string[];
    isdeprecated: string[];
    maintenance_status: string[];
    license_risk_level: string[];
    isexploitavailable: string[];
    isfixavailable: string[];
    isoutdated: string[];
    total_vulnerabilities: string[];
    avg_cvss_score: string[];
    overallscore: string[];
    vulnerabilityscore: string[];
    licensescore: string[];
    operationalriskscore: string[];
    technicaldebtscore: string[];
    cvssscore: string[];
    max_cvss_score: string[];
    critical_vulnerabilities: string[];
    high_vulnerabilities: string[];
    medium_vulnerabilities: string[];
    low_vulnerabilities: string[];
    total_license_issues: string[];
    avg_epss_score: string[];
    max_epss_score: string[];
    unique_license_types: string[];
    risk_score: string[];
    compliance_score: string[];
    security_maturity_score: string[];
  }>({
    risk_level: [],
    isdeprecated: [],
    maintenance_status: [],
    license_risk_level: [],
    isexploitavailable: [],
    isfixavailable: [],
    isoutdated: [],
    total_vulnerabilities: [],
    avg_cvss_score: [],
    overallscore: [],
    vulnerabilityscore: [],
    licensescore: [],
    operationalriskscore: [],
    technicaldebtscore: [],
    cvssscore: [],
    max_cvss_score: [],
    critical_vulnerabilities: [],
    high_vulnerabilities: [],
    medium_vulnerabilities: [],
    low_vulnerabilities: [],
    total_license_issues: [],
    avg_epss_score: [],
    max_epss_score: [],
    unique_license_types: [],
    risk_score: [],
    compliance_score: [],
    security_maturity_score: []
  });
  const [selectedColumns, setSelectedColumns] = useState<(keyof Component)[]>([
    'applicationName', 'projectName', 'scanVersion', 'name', 'version', 'license_spdx_identifier', 'isdeprecated', 'total_vulnerabilities', 'risk_level', 'homepageurl', 'githubstars', 'githubcontributors', 'githubforks'
  ]);
  const [selectedApplications, setSelectedApplications] = useState<(string | number)[]>([]);
  const [selectedProjects, setSelectedProjects] = useState<(string | number)[]>([]);
  const [applicationSearchTerm, setApplicationSearchTerm] = useState('');
  const [projectSearchTerm, setProjectSearchTerm] = useState('');

  const {
    data: applications,
    loading: applicationsLoading,

  } = useApplicationList(workgroupId);

  const {
    data: projects,
    loading: projectsLoading
  } = useProjectList(workgroupId,applicationId);

  const { data: scans = [] } = useScans({ workgroupId });
  const componentsOptions = useMemo(() => ({
    workgroupId,
    applicationIds: applicationId ? [applicationId] : selectedApplications.length > 0 ? selectedApplications.map(id => Number(id)) : undefined,
    projectIds: projectId ? [projectId] : selectedProjects.length > 0 ? selectedProjects.map(id => Number(id)) : undefined,
  } as FindComponentsOptions), [workgroupId, applicationId, projectId, selectedApplications, selectedProjects]);

  const { data: allComponents, loading, error, refetch } = useComponents(componentsOptions);

  useEffect(() => {
    const columnsParam = searchParams.get('columns');
    if (columnsParam) {
      const requestedColumns = columnsParam.split(',').map(col => col.trim()) as (keyof Component)[];
      const validColumns = requestedColumns.filter(col => 
        columns.some(c => c.key === col)
      );
      if (validColumns.length > 0) {
        setSelectedColumns(prev => [...new Set([...prev, ...validColumns])]);
      }
    }

    const filterParam = searchParams.get('filter');
    if (filterParam) {
      const newTableFilters = { ...tableFilters };
      const filterPairs = filterParam.split(';');
      
      filterPairs.forEach(pair => {
        const [key, value] = pair.split(':').map(s => s.trim());
        if (key && value) {
          const filterKey = key as keyof typeof tableFilters;
          const values = value.split(',').map(v => v.trim());
          console.log('Applying filter from URL:', filterKey, values);
          newTableFilters[filterKey]=values;
        
        }
      });
      
      setTableFilters(newTableFilters);
      setCurrentPage(1);
    }


    const searchParam = searchParams.get('search');
    if (searchParam) {
      setSearchTerm(searchParam);
      setCurrentPage(1);
    }
  }, []);

  const getNumericRangeOptions = (columnKey: keyof Component) => {
    if (!allComponents || !numericColumns.has(columnKey)) return [];

    const values = allComponents
      .map(row => Number(row[columnKey]) || 0)
      .filter(val => !isNaN(val) && val >= 0);

    if (values.length === 0) return [];

    const min = Math.min(...values);
    const max = Math.max(...values);

    if (min === max) return [{ label: `${min}`, value: `${min}` }];

    const ranges = [];

    if (columnKey.includes('score')) {
      if (max <= 10) {
        ranges.push(
          { label: '0-2', value: '0-2' },
          { label: '2-4', value: '2-4' },
          { label: '4-6', value: '4-6' },
          { label: '6-8', value: '6-8' },
          { label: '8-10', value: '8-10' }
        );
      } else {
        ranges.push(
          { label: '0-20', value: '0-20' },
          { label: '20-40', value: '20-40' },
          { label: '40-60', value: '40-60' },
          { label: '60-80', value: '60-80' },
          { label: '80-100', value: '80-100' }
        );
      }
    } else if (columnKey.includes('vulnerabilities') || columnKey.includes('issues')) {
      ranges.push(
        { label: '0', value: '0' },
        { label: '1-5', value: '1-5' },
        { label: '6-10', value: '6-10' },
        { label: '11-20', value: '11-20' },
        { label: '21+', value: '21+' }
      );
    } else {
      const step = Math.max(1, Math.ceil((max - min) / 5));
      for (let i = 0; i < 5; i++) {
        const start = min + (i * step);
        const end = i === 4 ? max : min + ((i + 1) * step) - 1;
        ranges.push({ label: `${start}-${end}`, value: `${start}-${end}` });
      }
    }

    return ranges.filter(range => {
      const [rangeMin, rangeMax] = range.value.includes('+')
        ? [parseInt(range.value), max]
        : range.value.includes('-')
          ? range.value.split('-').map(Number)
          : [Number(range.value), Number(range.value)];

      return values.some(val => val >= rangeMin && val <= rangeMax);
    });
  };


  const getTableCellValue = (row: Component, columnKey: keyof Component) => {
    const directValue = row[columnKey];
    if (directValue !== undefined && directValue !== null && directValue !== '') {
      return directValue;
    }

    switch (columnKey) {
      case 'applicationName':
        return row.applicationName ?? '-';
      case 'projectName':
        return row.projectName ?? '-';
      case 'scanVersion':
        return row.scanVersion ?? '-';
      case 'license_spdx_identifier':
        return row.license_spdx_identifier ?? row.licensename ?? '-';
      default:
        return directValue ?? '-';
    }
  };

  const enrichedComponents = useMemo(() => {
    const applicationMap = new Map((applications || []).map(app => [Number(app.id), app.name]));
    const projectMap = new Map((projects || []).map(project => [Number(project.id), project.name]));
    const scanMap = new Map((scans || []).map(scan => [Number(scan.id), scan]));

    return (allComponents || []).map(component => {
      const applicationName = component.applicationName ?? applicationMap.get(Number(component.applicationid)) ?? '-';
      const projectName = component.projectName ?? projectMap.get(Number(component.projectid)) ?? '-';
      const scanVersion = component.scanVersion ?? scanMap.get(Number(component.scanid))?.version ?? '-';

      return {
        ...component,
        applicationName,
        projectName,
        scanVersion,
      };
    });
  }, [allComponents, applications, projects, scans]);

  const filteredData = useMemo(() => {
    let _data = enrichedComponents;

    if (searchTerm) {
      const searchRegex = new RegExp(`\\b${searchTerm.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}`, 'i');
      _data = _data.filter(row => {
        const searchMatch =
          searchRegex.test(row.name) ||
          searchRegex.test(String(getTableCellValue(row, 'applicationName'))) ||
          searchRegex.test(String(getTableCellValue(row, 'projectName'))) ||
          searchRegex.test(String(getTableCellValue(row, 'scanVersion'))) ||
          searchRegex.test(row.version || '') ||
          searchRegex.test(row.license_spdx_identifier || row.licensename || '') ||
          searchRegex.test(row.description || '') ||
          searchRegex.test(row.homepageurl || '') ||
          searchRegex.test(row.repourl || '') ||
          searchRegex.test(row.maintenance_status || '') ||
          searchRegex.test(row.risk_level || '') ||
          searchRegex.test(row.license_risk_level || '');
        return searchMatch;
      });
    }

    _data = _data.filter(row => {
      return Object.keys(columnFilters).every(key => {
        const filterValue = columnFilters[key as keyof Component]?.toLowerCase();

        if (['risk_level', 'license_risk_level', 'isdeprecated', 'isexploitavailable', 'isfixavailable', 'isoutdated', 'maintenance_status'].includes(key) || numericColumns.has(key)) {
          return true;
        }

        if (!filterValue) return true;

        const rowValue = String(getTableCellValue(row, key as keyof Component) || '').toLowerCase();
        return rowValue.includes(filterValue);
      });
    });

    _data = _data.filter(row => {
      return Object.keys(tableFilters).every(key => {
        const filterValues = tableFilters[key as keyof typeof tableFilters];
        if (!filterValues || filterValues.length === 0) return true;

        if (scoreBasedColumns.has(key)) {
          const rowValue = Number(row[key as keyof Component]) || 0;
          const category = scoreToCategory(rowValue);
          return filterValues.some(val => val.toLowerCase() === category.toLowerCase());
        }

        if (numericColumns.has(key)) {
          const rowValue = Number(row[key as keyof Component]) || 0;
          return filterValues.some(rangeValue => {
            if (rangeValue.includes('+')) {
              const minValue = parseInt(rangeValue);
              return rowValue >= minValue;
            } else if (rangeValue.includes('-')) {
              const [min, max] = rangeValue.split('-').map(Number);
              return rowValue >= min && rowValue <= max;
            } else {
              return rowValue === Number(rangeValue);
            }
          });
        } else {
          let compareValue: string;

          switch (key) {
            case 'isdeprecated':
            case 'isexploitavailable':
            case 'isfixavailable':
            case 'isoutdated':
              compareValue = row[key as keyof Component] ? 'Yes' : 'No';
              break;
            case 'maintenance_status':
              compareValue = row[key as keyof Component] || 'Unknown';
              break;
            default:
              compareValue = row[key as keyof Component] || '';
          }

          return filterValues.some(val => val.toLowerCase() === compareValue.toLowerCase());
        }
      });
    });
    return _data;
   
  }, [allComponents, searchTerm, columnFilters, tableFilters]);

  const sortedData = useMemo(() => {
    let sortableItems = [...filteredData];
    if (sortConfig !== null) {
      sortableItems.sort((a, b) => {
        if (a[sortConfig.key] < b[sortConfig.key]) {
          return sortConfig.direction === 'asc' ? -1 : 1;
        }
        if (a[sortConfig.key] > b[sortConfig.key]) {
          return sortConfig.direction === 'asc' ? 1 : -1;
        }
        return 0;
      });
    }
    return sortableItems;
  }, [filteredData, sortConfig]);

  const handleSort = (key: keyof Component) => {
    let direction: 'asc' | 'desc' = 'asc';
    if (sortConfig && sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const handleColumnFilterChange = (key: keyof Component, value: string) => {
    setColumnFilters(prev => ({ ...prev, [key]: value }));
    setCurrentPage(1);
  };

  const handleSearchChange = (value: string) => {
    setSearchTerm(value);
    setCurrentPage(1);
  };


  const handleApplicationFilterChange = (applicationIds: (string | number)[]) => {
    setSelectedApplications(applicationIds);

    if (applicationIds.length > 0) {
      const filteredProjectIds = projects
        ?.filter(project => applicationIds.includes(project.applicationid))
        ?.map(project => project.id) || [];

      const validSelectedProjects = selectedProjects.filter(projectId =>
        filteredProjectIds.includes(Number(projectId))
      );

      if (validSelectedProjects.length !== selectedProjects.length) {
        setSelectedProjects(validSelectedProjects);
      }
    }

    setCurrentPage(1);
  };

  const handleApplicationSearch = (searchTerm: string) => {
    setApplicationSearchTerm(searchTerm);
  };

  const handleProjectFilterChange = (projectIds: (string | number)[]) => {
    setSelectedProjects(projectIds);

    if (projectIds.length > 0) {
      const requiredApplicationIds = new Set<string | number>();

      projects?.forEach(project => {
        if (projectIds.includes(project.id) && project.applicationid) {
          requiredApplicationIds.add(project.applicationid);
        }
      });

      const newApplicationIds = Array.from(requiredApplicationIds);

      const mergedApplicationIds = Array.from(new Set([...selectedApplications, ...newApplicationIds]));

      if (mergedApplicationIds.length !== selectedApplications.length) {
        setSelectedApplications(mergedApplicationIds);
      }
    }

    setCurrentPage(1);
  };

  const handleProjectSearch = (searchTerm: string) => {
    setProjectSearchTerm(searchTerm);
  };

  const handleTableFilterChange = (column: keyof typeof tableFilters, values: string[]) => {
    setTableFilters(prev => ({ ...prev, [column]: values }));
    setCurrentPage(1);
  };

  const clearAllFilters = () => {
    setTableFilters({
      risk_level: [],
      isdeprecated: [],
      maintenance_status: [],
      license_risk_level: [],
      isexploitavailable: [],
      isfixavailable: [],
      isoutdated: [],
      total_vulnerabilities: [],
      avg_cvss_score: [],
      overallscore: [],
      vulnerabilityscore: [],
      licensescore: [],
      operationalriskscore: [],
      technicaldebtscore: [],
      cvssscore: [],
      max_cvss_score: [],
      critical_vulnerabilities: [],
      high_vulnerabilities: [],
      medium_vulnerabilities: [],
      low_vulnerabilities: [],
      total_license_issues: [],
      avg_epss_score: [],
      max_epss_score: [],
      unique_license_types: [],
      risk_score: [],
      compliance_score: [],
      security_maturity_score: []
    });
    setColumnFilters({
      applicationName: '',
      projectName: '',
      scanVersion: '',
      name: '',
      version: '',
      license_spdx_identifier: '',
      isdeprecated: '',
      total_vulnerabilities: '',
      risk_level: '',
      homepageurl: '',
      avg_cvss_score: '',
      maintenance_status: '',
      overallscore: '',
      vulnerabilityscore: '',
      description: '',
      isexploitavailable: '',
      isfixavailable: '',
      isoutdated: '',
      repourl: '',
      licensescore: '',
      operationalriskscore: '',
      technicaldebtscore: '',
      license_risk_level: '',
      cvssscore: '',
      max_cvss_score: '',
      critical_vulnerabilities: '',
      high_vulnerabilities: '',
      medium_vulnerabilities: '',
      low_vulnerabilities: '',
      total_license_issues: '',
      avg_epss_score: '',
      max_epss_score: '',
      unique_license_types: '',
      risk_score: '',
      compliance_score: '',
      security_maturity_score: '',
    githubstars: '',
    githubcontributors: '',
    githubforks: ''
    });
    setSearchTerm('');
    setCurrentPage(1);
  };

  const handleColumnToggle = (columnKey: keyof Component) => {
    setSelectedColumns(prev =>
      prev.includes(columnKey)
        ? prev.filter(col => col !== columnKey)
        : [...prev, columnKey]
    );
  };


  const applicationOptions = useMemo(() =>
    applications?.map(app => ({
      value: app.id,
      label: app.name
    })) || [], [applications]);

  const projectOptions = useMemo(() => {
    if (!projects) return [];

    if (selectedApplications.length === 0) {
      return projects.map(project => {
        //const appName = applications?.find(app => app.id === project.applicationid)?.name || 'Unknown App';
        return {
          value: project.id,
          label: `${project.name}`
        };
      });
    }
    return projects
      .filter(project => selectedApplications.includes(project.applicationid))
      .map(project => {
        //const appName = applications?.find(app => app.id === project.applicationid)?.name || 'Unknown App';
        return {
          value: project.id,
          label: `${project.name}`
        };
      });
  }, [projects, selectedApplications, applications]);

  const itemsPerPage = 50;
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedData = sortedData.slice(startIndex, endIndex);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleComponentClick = (component: any) => {
    setSelectedComponent(component);
    setIsDetailsModalOpen(true);
  };


  const handleExportCSV = () => {
    const visibleColumns = columns.filter(col => selectedColumns.includes(col.key));
    const csv = [visibleColumns.map(c => c.label).join(",")].concat(
      sortedData.map(row =>
        visibleColumns.map(c => String(row[c.key])).join(",")
      )
    ).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "components.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  if (loading) {
    return (
      <div className="p-6 space-y-6">

        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
          <div className="text-muted-foreground">Loading components...</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6 space-y-6">

        <div className="text-center py-12">
          <div className="text-destructive">Error loading components: {error}</div>
          <Button onClick={refetch} className="mt-4">
            Try Again
          </Button>
        </div>
      </div>
    );
  }
  return (
    <div className="p-4 space-y-2 max-w-full" style={{ paddingTop: "10px" }}>

      <div className="flex flex-col gap-2">


        <div className="flex flex-col md:flex-row md:items-start gap-4">
          <div className="flex flex-col sm:flex-row sm:items-center gap-4 min-w-0 flex-1">
            <div className="relative w-full xs:flex-1 sm:max-w-xs lg:max-w-sm xl:max-w-md">
              <Search className="w-4 h-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground flex-shrink-0 z-10" />
              <Input
                placeholder="Search components..."
                value={searchTerm}
                onChange={(e) => handleSearchChange(e.target.value)}
                className="pl-10 w-full bg-white rounded-lg shadow-sm border-border text-sm"
                maxLength={200}
              />
            </div>

          {(!projectId && !applicationId) && (
              <div className="relative w-full xs:flex-1 sm:max-w-xs lg:max-w-sm xl:max-w-md">
                <MultiSelect
                  options={applicationOptions}
                  value={selectedApplications}
                  onChange={handleApplicationFilterChange}
                  onSearch={handleApplicationSearch}
                  placeholder="Filter by applications..."
                  searchPlaceholder="Search applications..."
                  loading={applicationsLoading}
                  maxDisplayed={2}
                  className="bg-background rounded-lg shadow-sm border-border"
                />

              </div>
            )}

            {!projectId && (
              <div className="relative w-full xs:flex-1 sm:max-w-xs lg:max-w-sm xl:max-w-md">
                <MultiSelect
                  options={projectOptions}
                  value={selectedProjects}
                  onChange={handleProjectFilterChange}
                  onSearch={handleProjectSearch}
                  placeholder={
                    selectedApplications.length > 0
                      ? `Filter projects (${projectOptions.length} available)...`
                      : "Filter by projects..."
                  }
                  searchPlaceholder="Search projects..."
                  loading={projectsLoading}
                  maxDisplayed={2}
                  className="bg-background rounded-lg shadow-sm border-border"
                />

              </div>
            )}

          </div>

          <div className="flex flex-col md:flex-row md:items-center gap-4 xl:shrink-0">

            <div className="flex flex-wrap items-center gap-2 shrink-0">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" className="rounded-lg shadow-sm border-border text-xs sm:text-sm whitespace-nowrap">
                    <Columns className="w-4 h-4 mr-1 sm:mr-2" />
                    <span>Columns</span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56 bg-popover">
                  <DropdownMenuLabel>Select Columns</DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  {columns.map(column => (
                    <DropdownMenuCheckboxItem
                      key={column.key}
                      checked={selectedColumns.includes(column.key)}
                      onCheckedChange={() => handleColumnToggle(column.key)}
                    >
                      {column.label}
                    </DropdownMenuCheckboxItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
              <EntitlementGate
                module="components"
                action="export"
                message="Component Hub export is available on the Professional plan and above."
              >
                <Button className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-lg shadow-lg shadow-primary/20 text-xs sm:text-sm whitespace-nowrap" onClick={handleExportCSV}>
                  <Download className="w-4 h-4 mr-1 sm:mr-2" />
                  <span>Export</span>
                </Button>
              </EntitlementGate>

            </div>
          </div>
        </div>

      </div>


      <div className="bg-card rounded-lg shadow border border-border relative max-w-full">
        <div className="overflow-auto max-h-[70vh] min-h-[400px] relative">
          <div className="min-w-full">
            <table className="w-full divide-y divide-border table-auto min-w-max relative">
              <thead className="bg-accent sticky top-0 z-10 shadow-sm border-b border-border">
                <tr>
                  {columns.filter(col => selectedColumns.includes(col.key)).map(col => (
                    <th key={col.key} className="px-2 sm:px-4 py-2 text-left text-xs font-semibold text-foreground uppercase tracking-wider min-w-[100px] bg-accent/95 backdrop-blur-sm">
                      <div className="flex flex-col space-y-2">
                        <div className="flex items-center space-x-1">
                          <span>{col.label}</span>
                          {col.sortable && (
                            <button
                              onClick={() => handleSort(col.key)}
                              className="ml-1 flex flex-col"
                            >
                              <ChevronUp
                                className={`w-3 h-3 ${sortConfig?.key === col.key && sortConfig?.direction === 'asc' ? 'text-primary' : 'text-muted-foreground'}`}
                              />
                              <ChevronDown
                                className={`w-3 h-3 -mt-1 ${sortConfig?.key === col.key && sortConfig?.direction === 'desc' ? 'text-primary' : 'text-muted-foreground'}`}
                              />
                            </button>
                          )}
                        </div>
                        {col.filterable && (
                          ['risk_level', 'isdeprecated', 'maintenance_status', 'license_risk_level', 'isexploitavailable', 'isfixavailable', 'isoutdated'].includes(col.key) || numericColumns.has(col.key) ? (
                            <div onClick={(e) => e.stopPropagation()}>
                              <MultiSelect
                                options={
                                  col.key === 'risk_level' || col.key === 'license_risk_level' ? [
                                    { label: 'Critical', value: 'Critical' },
                                    { label: 'High', value: 'High' },
                                    { label: 'Medium', value: 'Medium' },
                                    { label: 'Low', value: 'Low' }
                                  ] :
                                    col.key === 'maintenance_status' ? [
                                      { label: 'Active', value: 'Active' },
                                      { label: 'Recent', value: 'Recent' },
                                      { label: 'Inactive', value: 'Inactive' },
                                      { label: 'Unknown', value: 'Unknown' }
                                    ] :
                                      scoreBasedColumns.has(col.key) ? getScoreCategoryOptions() :
                                        numericColumns.has(col.key) ? getNumericRangeOptions(col.key) :
                                          [
                                            { label: 'Yes', value: 'Yes' },
                                            { label: 'No', value: 'No' }
                                          ]
                                }
                                value={tableFilters[col.key as keyof typeof tableFilters]}
                                onChange={(values) => handleTableFilterChange(col.key as keyof typeof tableFilters, values as string[])}
                                placeholder={`Filter ${col.label.toLowerCase()}...`}
                                searchPlaceholder={`Search ${col.label.toLowerCase()}...`}
                                maxDisplayed={2}
                                className="h-8 text-xs min-w-[120px]"
                                size="sm"
                              />
                            </div>

                          ) : (
                            <Input
                              placeholder={`Search ${col.label.toLowerCase()}...`}
                              value={columnFilters[col.key]}
                              onChange={(e) => handleColumnFilterChange(col.key, e.target.value)}
                              className="h-8 text-xs text-foreground bg-card rounded-lg border-border"
                              onClick={(e) => e.stopPropagation()}
                              maxLength={100}
                            />
                          )
                        )}
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="bg-card divide-y divide-border">
                {paginatedData.length > 0 ? (
                  paginatedData.map((row, idx) => (
                    <tr
                      key={idx}
                      className="hover:bg-accent"
                    >
                      {selectedColumns.map(colKey => {
                        const column = columns.find(c => c.key === colKey);
                        if (!column) return null;

                        const value = row[colKey];
                        let content: React.ReactNode;
                        let className = "px-2 sm:px-4 py-3 text-muted-foreground truncate max-w-[150px] sm:max-w-none";

                        switch (colKey) {
                          case 'applicationName':
                          case 'projectName':
                          case 'scanVersion':
                            className = "px-2 sm:px-4 py-3 font-medium text-foreground truncate max-w-[150px] sm:max-w-none";
                            content = <div className="truncate" title={String(getTableCellValue(row, colKey) ?? '')}>{getTableCellValue(row, colKey)}</div>;
                            break;
                          case 'name':
                            className = "px-2 sm:px-4 py-3 font-medium text-foreground truncate max-w-[150px] sm:max-w-none";
                            content = (
                              <div
                                className="truncate cursor-pointer hover:underline"
                                title={String(getTableCellValue(row, colKey) ?? '')}
                                onClick={() => handleComponentClick(row)}
                              >
                                {getTableCellValue(row, colKey)}
                              </div>
                            );
                            break;
                          case 'version':
                            className = "px-2 sm:px-4 py-3 text-muted-foreground truncate max-w-[100px] sm:max-w-none";
                            content = <div className="truncate" title={String(getTableCellValue(row, colKey) ?? '')}>{getTableCellValue(row, colKey)}</div>;
                            break;
                          case 'license_spdx_identifier': {
                            const licenseDisplay = value ?? row.licensename;
                            className = "px-2 sm:px-4 py-3 text-muted-foreground max-w-[140px] sm:max-w-none";
                            content = (
                              <div className="flex flex-col items-start gap-1">
                                <Badge style={{ whiteSpace: "wrap" }} className="bg-muted text-muted-foreground truncate max-w-full" title={licenseDisplay}>
                                  {licenseDisplay}
                                </Badge>
                                {row.licensemainid && (
                                  <button
                                    type="button"
                                    className="text-xs text-primary hover:underline"
                                    onClick={() => {
                                      setSelectedLicenseId(row.licensemainid);
                                      setIsObligationsModalOpen(true);
                                    }}
                                  >
                                    View Obligations
                                  </button>
                                )}
                              </div>
                            );
                            break;
                          }
                          case 'overallscore':
                          case 'licensescore':
                          case 'operationalriskscore':
                          case 'technicaldebtscore':
                          case 'risk_score':
                          case 'compliance_score':
                          case 'security_maturity_score':
                            className = "px-2 sm:px-4 py-3 text-muted-foreground truncate max-w-[120px] sm:max-w-none";
                            content = <div className="truncate" title={value?.toString()}>{value}</div>;
                            break;
                          case 'risk_level':
                          case 'license_risk_level':
                            content = (
                              <Badge
                                variant="secondary"
                                className={
                                  value === "Critical" ? "bg-red-100 text-red-800 border-red-200 dark:bg-red-900 dark:text-red-200 dark:border-red-700" :
                                    value === "High" ? "bg-orange-100 text-orange-800 border-orange-200 dark:bg-orange-900 dark:text-orange-200 dark:border-orange-700" :
                                      value === "Medium" ? "bg-yellow-100 text-yellow-800 border-yellow-200 dark:bg-yellow-900 dark:text-yellow-200 dark:border-yellow-700" :
                                        value === "Low" ? "bg-green-100 text-green-800 border-green-200 dark:bg-green-900 dark:text-green-200 dark:border-green-700" :
                                          "bg-gray-100 text-gray-800 border-gray-200 dark:bg-gray-900 dark:text-gray-200 dark:border-gray-700"
                                }
                              >
                                {value || 'Unknown'}
                              </Badge>
                            );
                            break;
                          case 'isdeprecated':
                          case 'isexploitavailable':
                          case 'isfixavailable':
                          case 'isoutdated':
                            content = (
                              <Badge
                                variant={value ? "destructive" : "secondary"}
                                className={value ? "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200" : "bg-muted text-muted-foreground"}
                              >
                                {value ? "Yes" : "No"}
                              </Badge>
                            );
                            break;
                          case 'total_vulnerabilities':
                          case 'critical_vulnerabilities':
                          case 'high_vulnerabilities':
                          case 'medium_vulnerabilities':
                          case 'low_vulnerabilities':
                          case 'total_license_issues':
                          case 'unique_license_types':
                            const count = value || 0;
                            content = (
                              <Badge
                                variant={count > 0 ? "destructive" : "secondary"}
                                className={count > 0 ? "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200" : "bg-muted text-muted-foreground"}
                              >
                                {count}
                              </Badge>
                            );
                            break;
                          case 'vulnerabilityscore':
                            className = "px-2 sm:px-4 py-3 text-muted-foreground truncate max-w-[120px] sm:max-w-none";
                            content = <div className="truncate" title={value?.toString()}>{value}</div>;
                            break;
                          case 'cvssscore':
                          case 'avg_cvss_score':
                          case 'max_cvss_score':
                          case 'avg_epss_score':
                          case 'max_epss_score':
                            const score = value || 0;
                            content = (
                              <Badge
                                variant="secondary"
                                className={
                                  score >= 8.1 ? "bg-red-100 text-red-800 border-red-200 dark:bg-red-900 dark:text-red-200 dark:border-red-700" :
                                    score >= 6.1 ? "bg-orange-100 text-orange-800 border-orange-200 dark:bg-orange-900 dark:text-orange-200 dark:border-orange-700" :
                                      score >= 4.1 ? "bg-yellow-100 text-yellow-800 border-yellow-200 dark:bg-yellow-900 dark:text-yellow-200 dark:border-yellow-700" :
                                        "bg-green-100 text-green-800 border-green-200 dark:bg-green-900 dark:text-green-200 dark:border-green-700"
                                }
                              >
                                {typeof score === 'number' ? score : score || '0.0'}
                              </Badge>
                            );
                            break;
                          case 'maintenance_status':
                            content = (
                              <Badge
                                variant={value === 'Active' ? "default" : "secondary"}
                                className={value === 'Active' ? "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200" : "bg-muted text-muted-foreground"}
                              >
                                {value || 'Unknown'}
                              </Badge>
                            );
                            break;
                          case 'homepageurl':
                          case 'repourl':
                            className = "px-4 py-3";
                            content = value ? (
                              <a
                                href={value}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 underline underline-offset-2 hover:underline-offset-4 transition-all duration-200 truncate block max-w-48"
                                title={value}
                              >
                                {value}
                              </a>
                            ) : (
                              <span className="text-muted-foreground">-</span>
                            );
                            break;
                          case 'description':
                            className = "px-2 sm:px-4 py-3 text-muted-foreground truncate max-w-[200px] sm:max-w-none";
                            content = <div className="truncate" title={value}>{value}</div>;
                            break;
                          case 'githubstars':
                          case 'githubcontributors':
                          case 'githubforks':
                            // null = not a GitHub component (or lookup failed): show a dash, not 0
                            content = typeof value === 'number'
                              ? <span>{value.toLocaleString()}</span>
                              : <span className="text-muted-foreground">-</span>;
                            break;
                          default:
                            className = "px-2 sm:px-4 py-3 text-muted-foreground truncate max-w-[150px] sm:max-w-none";
                            content = <div className="truncate" title={value?.toString()}>{value}</div>;
                            break;
                        }

                        return (
                          <td key={colKey} className={className}>
                            {content}
                          </td>
                        );
                      })}
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={selectedColumns.length} className="px-4 py-12 text-center">
                      <div className="text-muted-foreground">
                        {searchTerm || selectedApplications.length > 0 || selectedProjects.length > 0 ||
                          Object.values(tableFilters).some(arr => arr.length > 0) ||
                          Object.values(columnFilters).some(val => val !== '') ? (
                          <div className="space-y-2">
                            <div className="text-lg font-medium">No matching components found</div>
                            <div className="text-sm">Try adjusting your search criteria or filters</div>
                            <Button
                              variant="outline"
                              onClick={clearAllFilters}
                              className="mt-4"
                            >
                              Clear All Filters
                            </Button>
                          </div>
                        ) : (
                          <div className="space-y-2">
                            <div className="text-lg font-medium">No components found</div>
                            <div className="text-sm">Start by running a scan to discover components</div>
                          </div>
                        )}
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

      </div>

      <Paginator
        totalItems={sortedData.length}
        currentPage={currentPage}
        perPage={itemsPerPage}
        onPageChange={handlePageChange}
        className="mt-6"
      />

      <ComponentDetailsModal
        selectedComponent={selectedComponent}
        isDetailsModalOpen={isDetailsModalOpen}
        setIsDetailsModalOpen={setIsDetailsModalOpen}
      />

      <LicenseObligationsModal
        licenseId={selectedLicenseId}
        isOpen={isObligationsModalOpen}
        onOpenChange={setIsObligationsModalOpen}
      />
    </div>
  )
}
